import React from 'react';
import { Box, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CloseIcon from '@mui/icons-material/Close';
import useToken from '../../services/token';
import { mergeSpectralTasks, spectralTaskPresentation } from './spectralTaskProgress';
export default function GlobalSpectralBatchProgress() {
    const { token } = useToken();
    const [tasks, setTasks] = React.useState([]);
    const [connectionLost, setConnectionLost] = React.useState(false);
    React.useEffect(() => {
        setTasks([]);
        setConnectionLost(false);
        if (!token)
            return;
        const source = new EventSource(`/sse/${token}`);
        let disposed = false;
        source.addEventListener('spectral_batch', (e) => {
            if (disposed)
                return;
            try {
                const parsed = JSON.parse(e.data);
                if (Array.isArray(parsed.value) && parsed.value.every((task) => { var _a; return typeof (task === null || task === void 0 ? void 0 : task._id) === 'string' && typeof ((_a = task === null || task === void 0 ? void 0 : task._info) === null || _a === void 0 ? void 0 : _a.type) === 'string'; })) {
                    setTasks((previous) => mergeSpectralTasks(previous, parsed.value));
                    setConnectionLost(false);
                }
            }
            catch (_a) {
                // Malformed events do not erase the last known operation.
            }
        });
        source.onerror = () => { if (!disposed)
            setConnectionLost(true); };
        source.onopen = () => { if (!disposed)
            setConnectionLost(false); };
        return () => { disposed = true; source.close(); };
    }, [token]);
    if (tasks.length === 0)
        return null;
    return (React.createElement(Box, { sx: {
            position: 'fixed', bottom: 16, right: 16, zIndex: 2000,
            backgroundColor: '#fff', borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            p: 1.5, width: 360, maxWidth: 'calc(100vw - 32px)', maxHeight: '60vh', overflowY: 'auto'
        } },
        connectionLost && React.createElement(Typography, { role: "status", variant: "caption", color: "warning.main" }, "\u0421\u0432\u044F\u0437\u044C \u0441 \u043E\u0447\u0435\u0440\u0435\u0434\u044C\u044E \u043F\u043E\u0442\u0435\u0440\u044F\u043D\u0430. \u041F\u043E\u043A\u0430\u0437\u0430\u043D\u043E \u043F\u043E\u0441\u043B\u0435\u0434\u043D\u0435\u0435 \u0438\u0437\u0432\u0435\u0441\u0442\u043D\u043E\u0435 \u0441\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u0435; \u043E\u0436\u0438\u0434\u0430\u0435\u043C \u043F\u0435\u0440\u0435\u043F\u043E\u0434\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u044F."),
        tasks.map((task) => {
            var _a;
            const display = spectralTaskPresentation(task);
            const color = display.error ? 'error' : display.kind === 'quality' ? 'warning' : 'info';
            return React.createElement(Box, { key: task._id, role: "status", sx: { mb: 1.5, pl: 1, borderLeft: 3, borderColor: `${color}.main`, '&:last-child': { mb: 0 } } },
                React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center", sx: { mb: 0.5 } },
                    display.kind === 'quality' ? React.createElement(FactCheckIcon, { fontSize: "small", color: color }) : React.createElement(DownloadIcon, { fontSize: "small", color: color }),
                    React.createElement(Typography, { variant: "caption", fontWeight: 600, sx: { flex: 1 } }, display.title),
                    !task.active && React.createElement(IconButton, { size: "small", "aria-label": "\u0421\u043A\u0440\u044B\u0442\u044C \u0438\u0442\u043E\u0433 \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u0438", onClick: () => setTasks((previous) => previous.filter((item) => item._id !== task._id)) },
                        React.createElement(CloseIcon, { fontSize: "small" }))),
                display.location && React.createElement(Typography, { variant: "caption", display: "block", fontWeight: 600 }, display.location),
                React.createElement(Typography, { variant: "caption", display: "block", color: display.error ? 'error.main' : 'text.secondary' }, display.text),
                !display.terminal && React.createElement(LinearProgress, { color: color, variant: display.indeterminate ? 'indeterminate' : 'determinate', value: Number.isFinite(task._progress) ? Math.max(0, Math.min(100, task._progress)) : 0, sx: { my: 0.5, height: 6, borderRadius: 3 } }),
                !display.terminal && !display.indeterminate && React.createElement(Typography, { variant: "caption", color: "text.secondary" },
                    "\u041E\u0431\u0440\u0430\u0431\u043E\u0442\u0430\u043D\u043E \u0434\u0430\u0442 \u043F\u043E \u0438\u043D\u0434\u0435\u043A\u0441\u0430\u043C: ", (_a = task._info.done) !== null && _a !== void 0 ? _a : 0,
                    " \u0438\u0437 ",
                    task._info.total),
                display.outcome && React.createElement(Typography, { variant: "caption", display: "block", color: "text.secondary" }, display.outcome));
        })));
}
